import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './Delivery.module.css';

interface PhysicalBookOrder {
  id: number;
  project_id: number;
  book_id: number;
  created_at: string;
  updated_at: string;
  postal_code: string;
  address: string;
  address_detail: string | null;
  recipient_name: string;
  recipient_phone: string;
  delivery_state: string;
  delivery_state_label: string;
  cover_style_id: string;
  cover_style_name: string | null;
  cover_image_url: string | null;
  admin_note: string | null;
  project_title: string | null;
  book_status: string | null;
  user_id: string | null;
  // 주문한 사용자 정보 추가
  customer_name: string | null;
  order_user_id: string | null;
}

interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  start_index: number;
  end_index: number;
}

interface DeliveryResponse {
  orders: PhysicalBookOrder[];
  pagination: PaginationInfo;
}

const Delivery: React.FC = () => {
  const navigate = useNavigate();
  const { getDeliveries } = useApi();
  const [orders, setOrders] = useState<PhysicalBookOrder[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 검색 및 필터 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [deliveryState, setDeliveryState] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // 정렬 상태
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<string>('desc');

  // 배송 상태 옵션
  const deliveryStates = [
    { value: '', label: '전체 상태' },
    { value: 'ORDERED', label: '실물책 편집' },
    { value: 'PRINTING', label: '인쇄' },
    { value: 'SHIPPED', label: '배송' },
    { value: 'DELIVERED', label: '배송완료' },
    { value: 'CANCELLED', label: '주문취소' }
  ];

  // 실물책 주문 목록 조회
  const fetchOrders = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: page,
        page_size: pageSize,
        search: searchTerm || undefined,
        delivery_state: deliveryState || undefined,
        created_after: startDate || undefined,
        created_before: endDate || undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      const data = await getDeliveries(params);
      setOrders(data.orders);
      setPagination(data.pagination);
      setCurrentPage(page);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 정렬 처리 함수
  const handleSort = (field: string) => {
    let newSortOrder = 'desc';
    
    if (sortBy === field) {
      // 같은 필드를 클릭한 경우 정렬 순서만 변경
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // 다른 필드를 클릭한 경우 해당 필드로 정렬하고 내림차순으로 설정
      newSortOrder = 'desc';
    }
    
    setSortBy(field);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
    fetchOrders(1);
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchOrders();
  }, []);

  // 검색 및 필터 적용
  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders(1);
  };

  // 필터 초기화
  const handleReset = () => {
    setSearchTerm('');
    setDeliveryState('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setSortBy('created_at');
    setSortOrder('desc');
    fetchOrders(1);
  };

  // 페이지 변경
  const handlePageChange = (page: number) => {
    fetchOrders(page);
  };

  // 페이지 크기 변경
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchOrders(1);
  };

  // 상세 페이지로 이동
  const handleViewDetail = (orderId: number) => {
    navigate(`/admin/delivery/detail/${orderId}`);
  };

  // 사용자 상세 페이지로 이동
  const handleViewUserDetail = (userId: string | null) => {
    if (userId) {
      navigate(`/admin/users/detail/${userId}`, {
        state: { 
          from: '/admin/delivery',
          fromName: '실물책 주문관리'
        }
      });
    }
  };

  // 배송 상태별 배경색 반환
  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'ORDERED': return '#e3f2fd';
      case 'PRINTING': return '#fff3e0';
      case 'SHIPPED': return '#e8f5e8';
      case 'DELIVERED': return '#e8f5e8';
      case 'CANCELLED': return '#ffebee';
      default: return '#f5f5f5';
    }
  };

  // 배송 상태별 텍스트 색상 반환
  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'ORDERED': return '#1976d2';
      case 'PRINTING': return '#f57c00';
      case 'SHIPPED': return '#388e3c';
      case 'DELIVERED': return '#388e3c';
      case 'CANCELLED': return '#d32f2f';
      default: return '#757575';
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className={styles.deliveryContainer}>
        <div className={styles.loading}>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.deliveryContainer}>
        <div className={styles.error}>오류: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles.deliveryContainer}>
      <div className={styles.deliveryHeader}>
        <h1 className="admin-page-title">실물책 주문관리</h1>
      </div>

      {/* 검색 및 필터 */}
      <div className={styles.searchFilterSection}>
        <div className={styles.searchRow}>
          <div className={styles.searchGroup}>
            <input
              type="text"
              placeholder="주문자명, 수령인명, 전화번호, 주소로 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          
          <div className={styles.filterGroup}>
            <select
              value={deliveryState}
              onChange={(e) => setDeliveryState(e.target.value)}
              className={styles.filterSelect}
            >
              {deliveryStates.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className={styles.dateGroup}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={styles.dateInput}
              placeholder="시작일"
            />
            <span className={styles.dateSeparator}>~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={styles.dateInput}
              placeholder="종료일"
            />
          </div>
          
          <div className={styles.buttonGroup}>
            <button onClick={handleSearch} className={styles.searchBtn}>
              검색
            </button>
            <button onClick={handleReset} className={styles.resetBtn}>
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 페이지 크기 선택 */}
      <div className={'w-full flex justify-between'}>
        <div className={styles.pageSizeSection}>
          <label>
            페이지당 표시:
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={styles.pageSizeSelect}
            >
              <option value={10}>10개</option>
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
            </select>
          </label>
        </div>
      </div>

      {/* 주문 목록 테이블 */}
      <div className={styles.ordersTableContainer}>
        <table className={styles.ordersTable}>
          <thead>
            <tr>
              <th>번호</th>
              <th>주문ID</th>
              <th>주문자</th>
              <th>수령인</th>
              <th>연락처</th>
              <th>주소</th>
              <th>커버스타일</th>
              <th className={styles.sortableHeader} onClick={() => handleSort('delivery_state')}>
                배송상태
                <span className={styles.sortIcon}>
                  {sortBy === 'delivery_state' ? (sortOrder === 'desc' ? ' ▼' : ' ▲') : ' ↕'}
                </span>
              </th>
              <th className={styles.sortableHeader} onClick={() => handleSort('created_at')}>
                주문일
                <span className={styles.sortIcon}>
                  {sortBy === 'created_at' ? (sortOrder === 'desc' ? ' ▼' : ' ▲') : ' ↕'}
                </span>
              </th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr key={order.id} className={styles.orderRow}>
                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                <td>{order.id}</td>
                <td>
                  {order.customer_name ? (
                    <button
                      onClick={() => handleViewUserDetail(order.user_id)}
                      className={styles.userLinkBtn}
                      title="사용자 상세정보 보기"
                    >
                      {order.customer_name}
                    </button>
                  ) : (
                    'N/A'
                  )}
                </td>
                <td>{order.recipient_name}</td>
                <td>{order.recipient_phone}</td>

                <td className={styles.addressCell}>
                  <div className={styles.addressInfo}>
                    {order.postal_code} {order.address} {order.address_detail || ''}
                  </div>
                </td>
                <td>
                  {order.cover_style_name || 'N/A'}
                </td>
                <td>
                  <span
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: getStatusBackgroundColor(order.delivery_state),
                      color: getStatusTextColor(order.delivery_state)
                    }}
                  >
                    {order.delivery_state_label}
                  </span>
                </td>
                <td>
                  {order.created_at ? new Date(order.created_at).toLocaleDateString('ko-KR') : '-'}
                </td>
                <td>
                  <button
                    onClick={() => handleViewDetail(order.id)}
                    className={styles.viewDetailBtn}
                  >
                    상세보기
                  </button>
                </td>
              </tr>
            ))}
            
            {/* 빈행 채우기 */}
            {pagination && orders.length < pageSize && Array.from({ length: pageSize - orders.length }, (_, index) => (
              <tr key={`empty-${index}`} className={styles.emptyRow}>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {pagination && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            {pagination.start_index} - {pagination.end_index} / 총 {pagination.total_records}건
          </div>
          
          <div className={styles.paginationControls}>
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={!pagination.has_previous}
              className={styles.paginationBtn}
            >
              이전
            </button>
            
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`${styles.paginationBtn} ${pageNum === pagination.current_page ? styles.active : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={!pagination.has_next}
              className={styles.paginationBtn}
            >
              다음
            </button>
          </div>
        </div>
      )}

      {orders.length === 0 && !loading && (
        <div className={styles.noOrders}>
          <p>검색 조건에 맞는 실물책 주문이 없습니다.</p>
        </div>
      )}
    </div>
  );
};

export default Delivery;
